import { Box, Grid } from '@mui/material'
import { Typography } from '@carto/meridian-ds/components'
import { useAppDispatch, useAppSelector } from 'app/state'
import { setSettingsSnapshot } from 'features/analysis/state/analysisSlice'
import { AnalysisSettings, AnalysisType, OperationType } from 'features/analysis/types'
import { getEnrichedDatasetById } from 'features/builder/state/keplerGlSlice'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import AnalysisHeader from '../AnalysisHeader'
import AggregationFunctionSelector from './form/AggregationFunctionSelector'
import BaseSource from '../../Commons/Form/BaseSource'
import SecondSourceSelector from '../../Commons/AnalysisSource/Form/SourceSelector'
import SecondSourceColumnSelector from '../../Commons/AnalysisSource/Form/SourceColumnSelector'
import { generateIntersectAndAggregateQuery } from './queries'
import { IntersectAnalysisSettings } from './types'
import { AppFormGridColumn } from 'features/apps/ui/AppForm/appsFormStyle'

function IntersectForm({
  settings,
  onChange,
  onQueryChange
}: {
  settings: IntersectAnalysisSettings
  onChange: (analysis: AnalysisSettings) => void
  onQueryChange: (query: string | undefined) => void
}) {
  const dispatch = useAppDispatch()

  const sourceDatasetId = useAppSelector((state) => state.analysis.sourceDatasetId) || ''
  const sourceDataset = useAppSelector((state) => getEnrichedDatasetById(state, sourceDatasetId as string))

  const secondSourceTable = useAppSelector((state) => state.analysis.secondSourceTable)
  const [secondSource, setSecondSource] = useState<string | undefined>(settings?.secondSource || secondSourceTable?.id)
  const secondDataset =
    useAppSelector((state) => getEnrichedDatasetById(state, secondSource as string)) || secondSourceTable

  const [aggOperation, setAggOperation] = useState<OperationType | undefined>(
    settings?.operationFunction || OperationType.COUNT
  )
  const [secondSourceColumn, setSecondSourceColumn] = useState<string | undefined>(settings?.secondSourceColumn)
  const currentQuery = useAppSelector((state) => state.analysis.querySource?.query)

  useEffect(() => {
    if (aggOperation && sourceDataset && secondDataset) {
      onQueryChange(
        generateIntersectAndAggregateQuery({
          sourceDataset,
          secondDataset,
          operation: aggOperation,
          secondSourceColumn
        })
      )
    }
  }, [aggOperation, currentQuery, onQueryChange, secondDataset, secondSourceColumn, sourceDataset])

  useEffect(() => {
    return () => {
      const settings: IntersectAnalysisSettings = {
        mainSource: sourceDatasetId,
        operationFunction: aggOperation,
        secondSource,
        secondSourceColumn,
        analysisType: AnalysisType.Intersect
      }
      dispatch(setSettingsSnapshot(settings))
      onChange(settings)
    }
  }, [aggOperation, sourceDatasetId, dispatch, secondSource, secondSourceColumn, onChange])

  const handleSecondSourceChange = (source) => {
    setSecondSource(source)
    setSecondSourceColumn('')
  }

  return (
    <>
      <AnalysisHeader analysisType={AnalysisType.Intersect}>
        <Typography variant="body2" color="textSecondary">
          <FormattedMessage id="analysis.intersect.description" />
        </Typography>
      </AnalysisHeader>
      <Grid container spacing={4} mt={3} mb={2}>
        <AppFormGridColumn item sm={6}>
          <Box mb={1.5}>
            <Typography variant="subtitle2">
              <FormattedMessage id="analysis.intersect.data" />
            </Typography>
          </Box>
          <BaseSource />
          <SecondSourceSelector
            source={secondSource}
            showHelpText={true}
            onChange={handleSecondSourceChange}
            spatialIndexDatasetsAvailable={false}
          />
        </AppFormGridColumn>
        <Grid item sm={6}>
          <Box mb={1.5}>
            <Typography variant="subtitle2">
              <FormattedMessage id="analysis.intersect.params" />
            </Typography>
          </Box>
          <AggregationFunctionSelector operator={aggOperation as OperationType} onChange={setAggOperation} />
          <SecondSourceColumnSelector
            source={secondSource}
            column={secondSourceColumn}
            operation={aggOperation}
            placeholderId="analysis.addColumn.secondColumnPlaceholder2"
            onChange={setSecondSourceColumn}
          />
        </Grid>
      </Grid>
    </>
  )
}

export default IntersectForm
